import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { ImageInfo, Story } from "../../common/Common";
import StoryListItem from "./StoryListItem";
import styles from "./StoryView.module.css";
import ReportStory from "./story/ReportStory";
import StoryForm from "./StoryForm";
import ScrollView from "../ScrollView";
import { useCookies } from "react-cookie";
import { MiniButtonType } from "../MiniButton";
import BottomButtonBox from "../BottomButtonBox";
import { useModal } from "../../modal/Modal";
import ModalButtons from "../../modal/ModalButtons";
import DataProvider from "../../../data_provider/DataProvider";
import { ToastData, ToastType } from "../../common/Toast";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import { StoryReportReqBody } from "../../../data_provider/gateways/StoryGateway";

interface StoryViewProps {
    xid: number;
    mapId: string;
    setIsFullView: Dispatch<SetStateAction<boolean>>;
    isCreator: boolean;
    setToast?: React.Dispatch<React.SetStateAction<ToastData | undefined>>;
    setStoryImages: Dispatch<SetStateAction<ImageInfo[] | undefined>>;
    moveToGallery: () => void; // 갤러리로 이동하는 함수

    openMapScreenModal?: (modalProps: any) => void; // 지도 화면 모달 열기 함수
    closeMapScreenModal?: () => void; // 지도 화면 모달 닫기 함수
    isAdmin: boolean; // 관리자 여부
}

interface StoryScrollViewProps {}

export const enum ContentIdx {
    LIST,
    WRITE,
    REPORT,
    EDIT,
    DELETE,
}

/**
 * @author bakim
 * @remark 이야기 영역
 * @param { number } xid (required) 장소 고유 아이디
 * @param {Dispatch<SetStateAction<boolean>>} setIsFullView (required) 플로팅 사이드 시트내 전체 영역 차지 여부 설정 함수
 * @returns <StoryView>
 */

// 이야기 전용 스크롤 뷰
const StoryScrollView = (props: StoryScrollViewProps) => {
    useEffect(() => {}, []);

    return;
};

const StoryView = (props: StoryViewProps) => {
    const { ModalComponent, openModal, closeModal } = useModal();
    const isLogin = useLoginStatus();
    const formRef = useRef<HTMLFormElement>(null);
    const reportRef = useRef<HTMLFormElement | null>(null);
    const [cookies] = useCookies(["KMNM"]);
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);
    const [selectedStory, setSelectedStory] = useState<Story>();
    const [contentIdx, setContentIdx] = useState<ContentIdx>(ContentIdx.LIST);
    const [count, setCount] = useState<number>(0);
    const [formState, setFormState] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sort, setSort] = useState<"like" | "date">("date");
    const [refreshToken, setRefreshToken] = useState(0);
    const [isEmpty, setIsEmpty] = useState(true);

    const selectedIdxRef = useRef(selectedIdx);

    const [openMenuId, setOpenMenuId] = useState<number>(-1);

    // Ref
    const selectedSidRef = useRef(-1); // 선택된 이야기의 Sid 값을 저장

    useEffect(() => {
        selectedIdxRef.current = selectedIdx;
    }, [selectedIdx]);

    useEffect(() => {
        if (contentIdx === ContentIdx.LIST) {
            props.setIsFullView(false);
            setSelectedIdx(-1); // 선택된 이야기 인덱스 초기화
            setFormState(false); // 신고/수정/삭제 폼 상태 초기화
            setSelectedStory(undefined);
        } else {
            props.setIsFullView(true);
        }
    }, [contentIdx]);

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    const backToList = () => {
        setContentIdx(ContentIdx.LIST); // 리스트 이동
    };

    const deleteStory = async () => {
        const res = await DataProvider.DeleteStory({ mapid: props.mapId, XID: props.xid.toString(), SID: selectedIdx.toString() });
        props.setToast &&
            props.setToast({
                type: ToastType.DELETE,
                target: "이야기",
            });
        backToList();
    };

    const reqStoryData = useCallback(async (options: any) => {
        const res = await DataProvider.GetStoryList({ mapid: props.mapId, XID: props.xid.toString() }, options);
        res.flag && setCount(res.result.count);
        return res;
    }, []);

    const submitStory = async () => {
        if (!formRef.current) return;
        const form = new FormData(formRef.current);

        const content = form.get("content")?.toString().trim()
        if (content && content.length < 10) {
            props.setToast && props.setToast({
                type: ToastType.WARNING,
                message: "공백을 제외한 내용을 10자 이상 입력해주세요.",
            })
            return;
        }

        form.append("storyType", "story");
        form.getAll("images")
            .filter((f) => f instanceof File && f.size > 0 && f.name !== "")
            .forEach((file) => {
                // 시간
                console.log(file);
                const now = new Date();
                const pad = (num: number, size: number = 2) => num.toString().padStart(size, "0");
                const year = now.getFullYear();
                const month = pad(now.getMonth() + 1);
                const day = pad(now.getDate());
                const hour = pad(now.getHours());
                const min = pad(now.getMinutes());
                const sec = pad(now.getSeconds());
                const ms = pad(now.getMilliseconds(), 3);
                const imageName = props.mapId + "_" + year + month + day + hour + min + sec + ms + "_" + Math.random() + ".png";
                file instanceof File && form.append("imageNames", imageName);
            });

        try {
            const res = await DataProvider.AddStory({ mapid: props.mapId, XID: props.xid.toString() }, form);
            if (res.flag) {
                props.setToast &&
                    props.setToast({
                        type: ToastType.ADD,
                        target: "이야기",
                    });
                count && setCount(count + 1);
                setRefreshToken((t) => t + 1);
                backToList();
            }
        } catch (err) {
            console.log(err);
            showModal("이야기 추가에 실패했습니다.");
        }
    };

    const checkReportForm = () => {
        if (reportRef.current) {
            const checked = reportRef.current.querySelectorAll("input:checked");
            const submitReady = checked.length > 0 ? true : false;

            setFormState(submitReady);
        }
    };

    const submitReport = async () => {
        if (reportRef.current) {
            const form = new FormData(reportRef.current);
            const body: { reportType?: number } = {};
            form.forEach((value, key) => {
                body.reportType = Number(value) + 1;
            });

            try {
                const res = await DataProvider.ReportStory(
                    { mapid: props.mapId, XID: props.xid.toString(), SID: selectedIdx.toString() },
                    body as StoryReportReqBody
                );
                if (res.flag) {
                    props.setToast &&
                        props.setToast({
                            type: ToastType.REPORT,
                            target: "이야기",
                        });
                }
            } catch (err) {}
            setOpenMenuId(-1);
            setSelectedIdx(-1);
            backToList();
        }
    };

    const titleText = {
        [ContentIdx.LIST]: `남긴 이야기 ${count}건`,
        [ContentIdx.WRITE]: "이야기 작성",
        [ContentIdx.EDIT]: "이야기 수정",
        [ContentIdx.REPORT]: "이야기 신고",
        [ContentIdx.DELETE]: "이야기 삭제",
    };

    // 하단 버튼 설정
    const buttonOptions = {
        [ContentIdx.LIST]: {
            right: {
                label: "이야기 작성",
                type: isLogin ? MiniButtonType.BLUE : MiniButtonType.GRAY,
                onClick: () => {
                    isLogin ? setContentIdx(ContentIdx.WRITE) : setContentIdx(ContentIdx.LIST);
                },
            },
        },
        [ContentIdx.WRITE]: {
            left: { label: "취소", type: MiniButtonType.GRAY, onClick: backToList },
            right: { label: "확인", type: isEmpty? MiniButtonType.DISABLED : MiniButtonType.BLUE, onClick: submitStory },
        },
        [ContentIdx.EDIT]: {
            left: { label: "취소", type: MiniButtonType.GRAY, onClick: backToList },
            right: { label: "수정", type: MiniButtonType.BLUE, onClick: submitStory },
        },
        [ContentIdx.REPORT]: {
            left: { label: "취소", type: MiniButtonType.GRAY, onClick: backToList },
            right: {
                label: "신고",
                type: formState ? MiniButtonType.RED : MiniButtonType.GRAY,
                onClick: formState ? submitReport : undefined,
            },
        },
        [ContentIdx.DELETE]: {
            left: { label: "취소", type: MiniButtonType.GRAY, onClick: backToList },
            right: { label: "삭제", type: MiniButtonType.RED, onClick: deleteStory },
        },
    };

    // 스토리
    const renderStoryItem = (item: Story) => {
        const isActive = item.SID === openMenuId;

        let children: Story[] = [];

        switch (item.storyType) {
            case "story":
                children = item.comments ?? [];
                break;
            case "comment":
                children = item.replies ?? [];
                break;
            default:
                children = [];
        }

        return (
            <div>
                <StoryListItem
                    key={`item-${item.SID}`}
                    currentUser={cookies.KMNM ?? undefined}
                    sort={sort}
                    item={item}
                    isActive={isActive}
                    isLogin={isLogin}
                    selectedIdx={selectedIdx}
                    backToList={backToList}
                    mapId={props.mapId}
                    xid={props.xid}
                    selectedSidRef={selectedIdxRef}
                    setOpenMenuId={setOpenMenuId}
                    setIsLoading={setIsLoading}
                    setContentIdx={setContentIdx}
                    setSelectedIdx={setSelectedIdx}
                    setStoryImages={props.setStoryImages}
                    setToast={props.setToast}
                    moveToGallery={props.moveToGallery}
                    openMapScreenModal={props.openMapScreenModal}
                    closeMapScreenModal={props.closeMapScreenModal}
                    isAdmin={props.isAdmin}
                />
                {children.map((child) => (
                    <div key={child.SID}>{renderStoryItem(child)}</div>
                ))}
                {item.storyType === "story" && <div className={styles.line}>{""}</div>}
            </div>
        );
    };

    const renderContent = () => {
        switch (contentIdx) {
            case ContentIdx.WRITE:
                return (
                    <form ref={formRef} className={`${styles.form}`}>
                        <StoryForm setIsEmpty={setIsEmpty}/>
                    </form>
                );
            case ContentIdx.REPORT:
                return (
                    <form ref={reportRef} method="POST" encType="multipart/form-data">
                        <ReportStory checkForm={checkReportForm} />
                    </form>
                );
            case ContentIdx.EDIT:
                return (
                    <form ref={formRef} className={`${styles.form}`} method="POST" encType="multipart/form-data">
                        {selectedStory && <StoryForm data={selectedStory} />}
                    </form>
                );
            case ContentIdx.DELETE:
                return (
                    <div className={styles.content}>
                        <span>삭제한 이야기는 다시 복구할 수 없습니다. 정말 삭제하시겠습니까?</span>
                    </div>
                );
            default:
                return (
                    <ScrollView
                        key={refreshToken}
                        renderItem={renderStoryItem}
                        getData={reqStoryData}
                        isSorting={{ sortBy: sort, direction: "" }}
                        preserveScroll={true}
                    />
                );
        }
    };

    return (
        <>
            <ModalComponent />
            <div className={`${styles.container} undraggable`}>
                {contentIdx === ContentIdx.LIST && (
                    <div>
                        <div className={styles.title}>
                            <div className={styles[`title-container`]}>
                                <span className={styles.titleText}>남긴 이야기</span>
                                <span className={styles.titleCount}>{count}건</span>
                            </div>
                            <div className={styles[`sort-container`]}>
                                <div
                                    className={`${styles.sort} ${sort === "like" ? styles.sortSelected : styles.sortUnselected}`}
                                    onClick={() => {
                                        setSort("like");
                                    }}
                                >
                                    좋아요순
                                </div>
                                <div
                                    className={`${styles.sort} ${sort === "date" ? styles.sortSelected : styles.sortUnselected}`}
                                    onClick={() => {
                                        setSort("date");
                                    }}
                                >
                                    최신순
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {contentIdx === ContentIdx.WRITE && (
                    <div className={styles.title}>
                        <div className={styles[`title-container`]}>
                            <span className={styles.titleText}>이야기 작성</span>
                        </div>
                    </div>
                )}
                {contentIdx === ContentIdx.DELETE && (
                    <div>
                        <div className={styles.title}>
                            <div className={styles[`title-container`]}>
                                <span className={styles.titleText}>이야기 삭제</span>
                            </div>
                        </div>
                        {/* <div className={styles.line}/> */}
                    </div>
                )}
                {contentIdx === ContentIdx.REPORT && (
                    <div>
                        <div className={styles.title}>
                            <div className={styles[`title-container`]}>
                                <span className={styles.titleText}>이야기 신고</span>
                            </div>
                        </div>
                    </div>
                )}
                {renderContent()}
            </div>
            <div className={styles.button_area}>{<BottomButtonBox {...buttonOptions[contentIdx]} />}</div>
        </>
    );
};

export default StoryView;
